<template>
  <div class="e-date-picker">
    <v-menu v-model="isOpened" content-class="e-date-picker-content" :disabled="disabled">
      <template #activator="{ props: activatorProps }">
        <e-input
          class="e-date-picker__input"
          type="button"
          :class="inputClass"
          :disabled="disabled"
          :model-value="inputValue"
          :label="label"
          :rules="rules"
          :error-messages="errorMessages"
          v-bind="activatorProps"
        >
          <template #append-inner>
            <e-icon
              v-if="props.clearable && isOpened && inputValue"
              class="e-date-picker__icon"
              name="closeSvg"
              @click="clearValue"
            />
            <e-icon v-else class="e-date-picker__icon" name="calendar" />
          </template>
        </e-input>
      </template>
      <div class="e-date-picker__menu">
        <vue-date-picker
          v-model="modelValue"
          inline
          locale="ru-RU"
          class="e-date-picker__datepicker"
          :min-date="minDate"
          :max-date="maxDate"
          :auto-apply="!partialMultiple"
          :range="isRange"
          :multi-calendars="props.multiple"
          :enable-time-picker="false"
          @update:model-value="closeMenu"
          @range-start="onRangeStart"
          @range-end="onRangeEnd"
        />
        <div v-if="props.showPeriods" class="e-date-picker__periods">
          <e-btn
            :variant="selectedPeriod === 'last7Days' ? 'primary' : 'secondary'"
            size="M"
            @click="setPeriod('last7Days')"
          >
            Последние 7 дней
          </e-btn>
          <e-btn
            :variant="selectedPeriod === 'lastMonth' ? 'primary' : 'secondary'"
            size="M"
            @click="setPeriod('lastMonth')"
          >
            Последний месяц
          </e-btn>
          <e-btn
            :variant="selectedPeriod === 'currentMonth' ? 'primary' : 'secondary'"
            size="M"
            @click="setPeriod('currentMonth')"
          >
            Текущий месяц
          </e-btn>
          <e-btn
            :variant="selectedPeriod === 'previousMonth' ? 'primary' : 'secondary'"
            size="M"
            @click="setPeriod('previousMonth')"
          >
            Предыдущий месяц
          </e-btn>
          <e-btn
            :variant="selectedPeriod === 'lastYear' ? 'primary' : 'secondary'"
            size="M"
            @click="setPeriod('lastYear')"
          >
            Последний год
          </e-btn>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends Date | Date[] | undefined,
    R extends boolean | undefined = false,
    M extends boolean | undefined = false
  "
>
import VueDatePicker from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import { ref, toRef } from 'vue';
import type { InputRules } from '@/core/types/components/components';
import type { Dayjs } from 'dayjs';
import { EBtn, EIcon, EInput } from '@/core/components/ui';
import { getIsoDateFormat, stringToLocaleDate } from '@/core/utils/date';
import '@vuepic/vue-datepicker/dist/main.css';

const props = defineProps<{
  label?: string;
  inputClass?: string;
  multiple?: M;
  range?: R;
  partialMultiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  rules?: InputRules;
  errorMessages?: string | string[];
  min?: Date | Dayjs | string;
  max?: Date | Dayjs | string;
  showPeriods?: boolean;
}>();

type DatePickerModelValue = R extends true ? Date[] : M extends true ? Date[] : Date;

const modelValue = defineModel<DatePickerModelValue | undefined>();

const isOpened = ref(false);

const closeMenu = () => {
  isOpened.value = false;
};

const onRangeStart = (value: Date | null) => {
  if (!value || !Array.isArray(modelValue.value) || !props.partialMultiple) {
    return;
  }

  modelValue.value[0] = value;

  if (modelValue.value.length > 1) {
    modelValue.value.splice(1, 1);
  }
};

const onRangeEnd = (value: Date | null) => {
  if (!value || !Array.isArray(modelValue.value) || !props.partialMultiple) {
    return;
  }

  if (modelValue.value[0] && value < modelValue.value[0]) {
    modelValue.value[1] = modelValue.value[0];
    modelValue.value[0] = value;
  } else {
    modelValue.value[1] = value;
  }

  if (modelValue.value.length > 1) {
    closeMenu();
  }
};

const isRange = toRef(() => props.multiple || props.range);

const inputValue = toRef(() => {
  if (isRange.value) {
    return Array.isArray(modelValue.value) && modelValue.value[0]
      ? `${stringToLocaleDate(modelValue.value[0])} ${modelValue.value[1] ? ` – ${stringToLocaleDate(modelValue.value[1])}` : modelValue.value[0] ? '- Hе ограничено' : ''}`
      : '';
  } else {
    return modelValue.value && !Array.isArray(modelValue.value) ? stringToLocaleDate(modelValue.value) : '';
  }
});

const clearValue = () => {
  if (isRange.value && Array.isArray(modelValue.value)) {
    modelValue.value = [] as unknown as DatePickerModelValue;
    return;
  }

  modelValue.value = undefined;
};

const maxDate = toRef(() => (dayjs.isDayjs(props.max) ? getIsoDateFormat(props.max) : props.max));
const minDate = toRef(() => (dayjs.isDayjs(props.min) ? getIsoDateFormat(props.min) : props.min));

const setPeriod = (period: 'last7Days' | 'lastMonth' | 'currentMonth' | 'previousMonth' | 'lastYear') => {
  if (!props.range) {
    return;
  }

  switch (period) {
    case 'last7Days':
      modelValue.value = [dayjs().subtract(6, 'day').toDate(), dayjs().toDate()] as unknown as DatePickerModelValue;
      break;
    case 'lastMonth':
      modelValue.value = [dayjs().subtract(1, 'month').toDate(), dayjs().toDate()] as unknown as DatePickerModelValue;
      break;
    case 'currentMonth':
      modelValue.value = [
        dayjs().startOf('month').toDate(),
        dayjs().endOf('month').toDate(),
      ] as unknown as DatePickerModelValue;
      break;
    case 'previousMonth':
      modelValue.value = [
        dayjs().subtract(1, 'month').startOf('month').toDate(),
        dayjs().subtract(1, 'month').endOf('month').toDate(),
      ] as unknown as DatePickerModelValue;
      break;
    case 'lastYear':
      modelValue.value = [dayjs().subtract(1, 'year').toDate(), dayjs().toDate()] as unknown as DatePickerModelValue;
      break;
  }
};

const selectedPeriod = toRef(() => {
  if (!modelValue.value || !Array.isArray(modelValue.value)) {
    return null;
  }

  if (
    dayjs(modelValue.value[0]).isSame(dayjs().subtract(6, 'day'), 'day') &&
    dayjs(modelValue.value[1]).isSame(dayjs(), 'day')
  ) {
    return 'last7Days';
  }

  if (
    dayjs(modelValue.value[0]).isSame(dayjs().subtract(1, 'month'), 'day') &&
    dayjs(modelValue.value[1]).isSame(dayjs(), 'day')
  ) {
    return 'lastMonth';
  }

  if (
    dayjs(modelValue.value[0]).isSame(dayjs().startOf('month'), 'day') &&
    dayjs(modelValue.value[1]).isSame(dayjs().endOf('month'), 'day')
  ) {
    return 'currentMonth';
  }

  if (
    dayjs(modelValue.value[0]).isSame(dayjs().subtract(1, 'month').startOf('month'), 'day') &&
    dayjs(modelValue.value[1]).isSame(dayjs().subtract(1, 'month').endOf('month'), 'day')
  ) {
    return 'previousMonth';
  }

  if (
    dayjs(modelValue.value[0]).isSame(dayjs().subtract(1, 'year'), 'day') &&
    dayjs(modelValue.value[1]).isSame(dayjs(), 'day')
  ) {
    return 'lastYear';
  }

  return null;
});
</script>

<style scoped lang="scss">
:global(.dp__main) {
  --dp-font-family: #{$main-font-family};
  --dp-menu-padding: 13px 16px;
  --dp-cell-padding: 5px 11px;
  --dp-button-icon-height: 16px;
  --dp-cell-size: 40px;
  --dp-row-margin: 4px 0;
  --dp-icon-color: #{$text-secondary-color};
  --dp-text-color: #{$text-primary-color};
  --dp-range-between-dates-text-color: #{$text-primary-color};
  --dp-hover-color: #{$primary-tonal-color};

  @include sm-and-down {
    --dp-cell-size: 30px;
    --dp-menu-padding: 8px;
    --dp-menu-min-width: 200px;
    --dp-row-margin: 2px 0;
    --dp-month-year-row-height: 26px;
  }
}

.e-date-picker {
  position: relative;

  width: 100%;

  &__icon {
    color: $text-secondary-color;
  }

  &__input {
    :deep(input) {
      text-align: left;
      color: $text-primary-color !important;
    }

    :deep(.v-field__append-inner) {
      .e-date-picker__icon {
        cursor: pointer;
      }
    }
  }

  &__menu {
    display: flex;
    align-items: flex-start;
    gap: 16px;

    padding: 16px;
    overflow: hidden;

    background-color: $main-bg-color;
    border-radius: $border-radius-l;
    box-shadow: 0 4px 8px 1px #00000014;

    @include xs {
      flex-direction: column;
    }
  }

  &__datepicker {
    width: fit-content;
  }

  &__periods {
    display: flex;
    flex-direction: column;
    gap: 8px;

    button {
      width: 100%;
    }

    @include xs {
      width: 100%;
    }
  }
}

:deep(.e-date-picker-content) {
  * {
    outline: none;
  }

  .dp {
    &__menu {
      font-family: $main-font-family;

      border: none;

      &_inner {
        gap: 6px;

        padding: 0;
      }

      & > div {
        width: 100%;

        .dp__instance_calendar {
          width: fit-content;

          @include sm-and-down {
            width: 100%;
          }
        }
      }
    }

    &__overlay {
      font-weight: 600;
      color: $text-secondary-color;
    }

    &__action_row {
      display: none;
    }

    &__month_year_wrap {
      .dp__month_year_wrap {
        width: fit-content;
        margin: 0 auto;
      }

      .dp__month_year_select {
        padding: 0 2px;
      }
    }

    &__calendar {
      font-size: 12px;

      &_header {
        margin-top: 8px;
        margin-bottom: 10px;

        @include sm-and-down {
          margin: 4px 0 0;
        }

        &_item {
          height: fit-content;

          font-size: 12px;
          font-weight: 600;
          color: $text-secondary-color;
          text-transform: uppercase;
        }
      }
    }

    &__calendar_header_separator {
      display: none;
    }

    &__cell {
      &_inner {
        border: none;
        border-radius: 50% !important;

        transition: none !important;

        @include sm-and-down {
          width: 100%;
        }

        &:after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;

          border-radius: 50%;

          @include sm-and-down {
            right: auto;
            left: 50%;

            aspect-ratio: 1;

            transform: translateX(-50%);
          }
        }
      }

      &_offset {
        font-weight: 400 !important;
        color: $text-inactive-color !important;

        background: transparent !important;

        &:after {
          display: none;
        }
      }
    }

    &__active_date {
      font-weight: 600;
    }

    &__today {
      position: relative;

      font-weight: 600;
      color: $primary-color;

      &.dp__active_date {
        color: var(--dp-primary-text-color);
      }

      &:after {
        border: 1px solid $primary-color;
      }
    }

    &__date_hover {
      z-index: 2;

      font-weight: 600;

      &:after {
        z-index: -1;

        background-color: $primary-tonal-color;
      }
    }

    &__range {
      &_between {
        background-color: $primary-light-color;
        border-radius: 0 !important;
      }
    }

    &__range_start,
    &__range_end {
      color: $white-color;
    }

    &__range_start,
    &__date_hover_start {
      background: linear-gradient(
        to right,
        transparent 0%,
        transparent 50%,
        $primary-light-color 50%,
        $primary-light-color 100%
      );
    }

    &__range_end,
    &__date_hover_end {
      background: linear-gradient(
        to left,
        transparent 0%,
        transparent 50%,
        $primary-light-color 50%,
        $primary-light-color 100%
      );
    }

    &__range_start.dp__range_end {
      background: transparent !important;
    }

    &__range_end,
    &__range_start,
    &__date_hover_end,
    &__date_hover_start {
      position: relative;
      z-index: 1;

      font-weight: 600;

      border-radius: 0 !important;

      &:after {
        z-index: -1;

        background-color: $primary-color;
      }
    }

    &__date_hover_end,
    &__date_hover_start {
      &:after {
        background-color: $primary-tonal-color;
      }
    }

    &__month_year_wrap {
      --dp-month-year-row-button-size: 32px;
      --dp-icon-color: #{$text-secondary-color};
      --dp-hover-icon-color: #{$text-secondary-color};

      .dp__month_year_select {
        padding: 0 4px;

        border-radius: $input-border-radius;
      }
    }

    &__button_bottom {
      color: $primary-color;

      border-radius: $input-border-radius;
    }

    &__overlay_cell {
      border-radius: $input-border-radius;

      &_active {
        color: $text-secondary-color;

        background-color: $primary-tonal-color;
        border-radius: $input-border-radius;
      }
    }
  }

  button {
    font-weight: 600;
    color: $btn-link-text-color;
  }
}
</style>
